<template>
<v-container class="pa-0 mt-10" v-show="abstracts.length > 0">
    <v-card>
        <v-row class="primary-header ma-0 pa-3" style="font-size: 25px" align="center">
            <span class="mr-3">백테스트 요약보고서</span>
            <v-spacer />
            <v-btn color="green" small @click="copyToClipboard" :loading="copyLoading">이미지로 복사</v-btn>
            <v-btn color="green" small @click="downloadAbstract">엑셀 다운로드</v-btn>
        </v-row>

        <v-col ref="report" style="margin: auto;">
            <div>
                <return-table v-model="abstracts" @select="onReturnSelected" />
            </div>

            <div class="my-5 report-container">
                <v-row class="ma-0 graph-header" justify="space-between" align="center">
                    <span>월별 자산현황</span>
                    <v-switch class="ma-0 pa-0" v-model="growthScale" dark hide-details label="로그스케일" />
                </v-row>
                <growth-chart class="pa-2" :chart-data="growthData" :scale="growthScale" :height="400" />
            </div>

            <div class="my-5 report-container">
                <div class="graph-header">월별 낙폭 (drawdowns)</div>
                <drawdown-chart class="pa-2" :chart-data="drawdownData" :height="400" />
            </div>

            <div class="my-5 report-container">
                <div class="graph-header">연도별 수익률</div>
                <return-chart class="pa-2" :chart-data="returnData" :height="400" />
            </div>
        </v-col>
    </v-card>

    <v-card>
        <v-row class="primary-header ma-0 pa-3" style="font-size: 25px" align="center">
            <span class="mr-3">백테스트 상세보고서</span>
            <v-spacer />
            <v-btn color="green" small @click="downloadDetails">엑셀 다운로드</v-btn>
        </v-row>
        <v-col v-for="item in detailReports" :key="`detail-report-${item[0]}`" style="margin: auto">
            <v-card class="mt-3 mb-3">
                <v-card-title class="primary-header">
                    <span style="font-size: 20px">{{ item[0] }}</span>
                </v-card-title>
                <v-card-text>
                    <detail-report v-model="item[1]" :interval="item[0]" :sort="sort" :order="order" />
                </v-card-text>
            </v-card>
        </v-col>
    </v-card>

    <report-dialog ref="reportDialog" />
</v-container>
</template>

<script>
import { maxDate } from '@/constants'
import XLSX from 'xlsx'
import ReturnTable from '@/views/components/ReturnTable'
import GrowthChart from '@/views/charts/GrowthChart'
import ReturnChart from '@/views/charts/ReturnChart'
import DrawdownChart from '@/views/charts/DrawdownChart'
import DetailReport from '@/views/components/DetailReport'
import ReportDialog from '@/views/dialogs/ReportDialog'
import {
    IndexData
} from '@/constants'
import {
    Calculator
} from '@/mixins/calculator.js'
import * as _ from 'lodash'

const moment = require('moment')
const html2canvas = require('html2canvas')

export default {
    name: 'asset-allocation-report',
    components: {
        ReturnTable,
        GrowthChart,
        ReturnChart,
        DrawdownChart,
        DetailReport,
        ReportDialog,
    },
    data() {
        return {
            assets: [],
            prices: [],
            startCash: 0,
            start: 2001,
            end: 2021,
            month: 1,
            period: 'yearly',
            results: {},
            sort: [],
            order: [],
            abstracts: [],
            growthData: {},
            growthScale: false,
            returnData: {},
            drawdownData: {},
            detailReports: [],
            copyLoading: false
        }
    },
    methods: {
        updateReport(assets, prices, cash, start, end, month, period) {
            if (prices.length == 0) return

            this.assets = assets
            this.prices = prices
            this.startCash = cash.replace(/,/gi, "")
            this.start = start
            this.end = end
            this.month = month
            this.period = period
            this.buildResults()

            const sorted = this.sortData(this.results)
            const sortedAndFiltered = sorted.filter(x => !x[0].includes('before'))
            const monthInterval = Calculator.getMonthInterval(
                sortedAndFiltered[0][0].split('-'),
                sortedAndFiltered[sortedAndFiltered.length - 1][0].split('-')
            )

            this.buildDetailReports(sorted)

            const monthlyPort = []
            for (const [date, value1] of sortedAndFiltered) {
                if (Object.keys(value1).length == 0) continue

                // if (date.startsWith('2021')) continue

                let amount_sum = 0
                for (const [code, value2] of Object.entries(value1)) {
                    amount_sum += value1[code].amount
                }

                monthlyPort.push(amount_sum)
            }

            const startCash = this.startCash * 10000
            const monthAxis = sortedAndFiltered.map(x => x[0])

            const portMonthReturns = []
            for (let i = 1; i < monthAxis.length; i++) portMonthReturns.push(monthlyPort[i] / monthlyPort[i - 1] - 1)

            const monthlyKospi = IndexData.getIndexMonthly(startCash, monthAxis, 'kospi')
            const monthlyKosdaq = IndexData.getIndexMonthly(startCash, monthAxis, 'kosdaq')

            const kospiMonthReturns = IndexData.getIndexMonthlyReturns(monthAxis, 'kospi')
            const kosdaqMonthReturns = IndexData.getIndexMonthlyReturns(monthAxis, 'kosdaq')

            const portMonthDrawdowns = Calculator.getDrawdowns(portMonthReturns)
            const kospiMonthDrawdowns = Calculator.getDrawdowns(kospiMonthReturns)
            const kosdaqMonthDrawdowns = Calculator.getDrawdowns(kosdaqMonthReturns)

            const yearAxis = this.detailReports.map(v => v[0])
            const portYearReturns = Calculator.buildYearReturns(monthlyPort)
            const kospiYearReturns = Calculator.buildYearReturns(monthlyKospi)
            const kosdaqYearReturns = Calculator.buildYearReturns(monthlyKosdaq)

            this.abstracts = [
                Calculator.buildAbstractResult('내 자산', startCash, monthInterval, monthlyPort, portYearReturns, portMonthDrawdowns),
                Calculator.buildAbstractResult('KOSPI', startCash, monthInterval, monthlyKospi, kospiYearReturns, kospiMonthDrawdowns),
                Calculator.buildAbstractResult('KOSDAQ', startCash, monthInterval, monthlyKosdaq, kosdaqYearReturns, kosdaqMonthDrawdowns)
            ]

            this.growthData = {
                labels: monthAxis,
                datasets: [{
                    label: '내 자산',
                    borderColor: '#f87979',
                    backgroundColor: '#f87979',
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    fill: false,
                    data: monthlyPort.map(x => x.toFixed(0))
                }, {
                    label: 'KOSPI',
                    borderColor: '#accbfc',
                    backgroundColor: '#accbfc',
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    fill: false,
                    data: monthlyKospi.map(x => x.toFixed(0))
                }, {
                    label: 'KOSDAQ',
                    borderColor: '#80eb34',
                    backgroundColor: '#80eb34',
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    fill: false,
                    data: monthlyKosdaq.map(x => x.toFixed(0))
                }]
            }

            this.drawdownData = {
                labels: monthAxis,
                datasets: [{
                    label: '내 자산',
                    borderColor: '#f87979',
                    backgroundColor: '#f87979',
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    data: portMonthDrawdowns
                }, {
                    label: 'KOSPI',
                    borderColor: '#accbfc',
                    backgroundColor: '#accbfc',
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    data: kospiMonthDrawdowns
                }, {
                    label: 'KOSDAQ',
                    borderColor: '#80eb34',
                    backgroundColor: '#80eb34',
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    data: kosdaqMonthDrawdowns
                }]
            }

            this.returnData = {
                labels: yearAxis,
                datasets: [{
                    label: '내 자산',
                    borderColor: '#f87979',
                    backgroundColor: '#f87979',
                    data: portYearReturns.map(x => x.toFixed(2))
                }, {
                    label: 'KOSPI',
                    borderColor: '#accbfc',
                    backgroundColor: '#accbfc',
                    data: kospiYearReturns.map(x => x.toFixed(2))
                }, {
                    label: 'KOSDAQ',
                    borderColor: '#80eb34',
                    backgroundColor: '#80eb34',
                    data: kosdaqYearReturns.map(x => x.toFixed(2))
                }]
            }
        },
        buildResults() {
            const dates = []
            const start = parseInt(this.start)
            const end = parseInt(this.end)

            for (let year = start; year <= end + 1; year++) {
                for (let month = 1; month <= 12; month++) {
                    const date = moment(`${year}-${month}-01`)

                    if (year == start && month < this.month) continue

                    if (year == end + 1 && month > this.month) break

                    if (date.isSameOrAfter(maxDate)) break

                    dates.push(date)
                }
            }

            this.results = {}
            for (const date of dates) {
                const prevDate = moment(date).subtract(1, 'months').format('YYYY-MM')
                const curDate = date.format('YYYY-MM')
                const prevPrices = this.prices.filter(v => v.date.startsWith(prevDate))
                const curPrices = this.prices.filter(v => v.date.startsWith(curDate))

                if (curPrices.length == 0 && !this.assets.find(a => a.code == '000000')) continue

                const totalPercent = this.getTotalPercent(curPrices)

                if (this.isRebalanceMonth(date.month() + 1)) {
                    this.results[curDate] = {}
                    let total = 0

                    if (Object.keys(this.results).length == 1) {
                        total = this.startCash * 10000
                    } else {
                        this.proceedOneMonth(prevDate, prevPrices, curDate, curPrices)

                        this.results[`${curDate}-before`] = Object.assign({}, this.results[curDate])

                        for (const [key, v] of Object.entries(this.results[`${curDate}-before`])) total += v.amount
                    }
                    
                    if (date.year() > end && date.month() + 1 > this.month) break

                    this.results[curDate] = {}
                    for (const price of curPrices) {
                        const asset = this.assets.find(a => a.code == price.code)
                        this.results[curDate][price.code] = {
                            name: asset.name,
                            amount: total * asset.percent / totalPercent
                        }
                    }

                    const cashAsset = this.assets.find(a => a.code == '000000')
                    if (cashAsset) {
                        this.results[curDate]['000000'] = {
                            name: cashAsset.name,
                            amount: total * cashAsset.percent / totalPercent
                        }
                    }
                } else {
                    if (Object.keys(this.results).length == 0) continue

                    this.results[curDate] = {}
                    this.proceedOneMonth(prevDate, prevPrices, curDate, curPrices)
                }
            }
        },
        isRebalanceMonth(currentMonth) {
            const months = [this.month]

            if (this.period == 'monthly') return true
            else if (this.period == 'quarterly') {
                for (let i = 0; i < 3; i++) {
                    const quotient = Math.floor((months[i] + 3) / 13)
                    let month = (months[i] + 3) % 13
                    if (quotient > 0) month += 1
                    months.push(month)
                }
            } else if (this.period == 'semi-annually') {
                for (let i = 0; i < 1; i++) {
                    const quotient = Math.floor((months[i] + 6) / 13)
                    let month = (months[i] + 6) % 13
                    if (quotient > 0) month += 1
                    months.push(month)
                }
            }

            return months.includes(currentMonth)
        },
        proceedOneMonth(prevDate, prevPrices, curDate, curPrices) {
            // 주가 반영
            for (const code of Object.keys(this.results[prevDate])) {
                if (code == '000000') {
                    this.results[curDate][code] = {
                        amount: this.results[prevDate][code].amount
                    }
                    continue
                }

                const curPrice = curPrices.find(v => v.code == code)
                const prevPrice = prevPrices.find(v => v.code == code)

                if (!prevPrice || !curPrice) continue

                this.results[curDate][code] = {
                    amount: this.results[prevDate][code].amount * curPrice.price / prevPrice.price
                }
            }

            // 정리매매
            const totalPercent = this.getTotalPercent(curPrices)
            for (const code of Object.keys(this.results[prevDate])) {
                if (code == '000000') continue

                const curPrice = curPrices.find(v => v.code == code)

                if (!curPrice) {
                    const amount = this.results[prevDate][code].amount
                    for (const asset of this.assets) {
                        if (asset.code == code) continue
                        
                        this.results[curDate][asset.code].amount += amount * asset.percent / totalPercent
                    }
                }
            }
        },
        getTotalPercent(curPrices) {
            return this.assets.filter(a => curPrices.map(p => p.code).includes(a.code) || a.code == '000000').map(a => parseFloat(a.percent)).reduce((a, b) => a + b)
        },
        onReturnSelected(selected) {
            for (const graphItem of this.growthData.datasets) graphItem.hidden = !selected.map(x => x.name).includes(graphItem.label)
            for (const graphItem of this.returnData.datasets) graphItem.hidden = !selected.map(x => x.name).includes(graphItem.label)
            for (const graphItem of this.drawdownData.datasets) graphItem.hidden = !selected.map(x => x.name).includes(graphItem.label)

            this.growthData = Object.assign({}, this.growthData)
            this.returnData = Object.assign({}, this.returnData)
            this.drawdownData = Object.assign({}, this.drawdownData)
        },
        sortData(rawData) {
            const sorted = Object.entries(rawData).sort((a, b) => {
                let year1 = parseInt(a[0].substring(0, 4))
                let year2 = parseInt(b[0].substring(0, 4))
                if (year1 > year2) return 1
                else if (year1 < year2) return -1
                else {
                    let month1 = parseInt(a[0].substring(5))
                    let month2 = parseInt(b[0].substring(5))
                    return month1 - month2
                }
            })

            return sorted
        },
        buildDetailReports(sorted) {
            this.detailReports = []

            let startDate = moment(sorted[0][0]).startOf('month')
            while (sorted.filter(v => moment(v[0]).isSameOrAfter(startDate)).length > 1) {
                const endDate = moment(startDate).add(13, 'months')

                const yearData = _.cloneDeep(sorted.filter(v => {
                    const date = moment(v[0].replace("-before", "")).endOf('month')
                    return date.isBetween(startDate, endDate)
                })) 

                for (let i = 0; i < yearData.length; i++) {
                    const data = yearData[i]
                    if (data[0].includes("-before")) {
                        const temp = yearData[i - 1]
                        temp[0] = `${temp[0]} (리밸런싱)`
                        yearData[i - 1] = data
                        yearData[i] = temp
                    }
                }

                if (yearData[0][0].includes('-before')) yearData.splice(0, 1)

                yearData.filter(v => v[0].includes('-before')).forEach(v => v[0] = v[0].replace('-before', ''))

                endDate.subtract(1, 'months')
                this.detailReports.push([`${startDate.format("YYYY-MM")} ~ ${endDate.format("YYYY-MM")}`, yearData])
                startDate = endDate
            }
        },
        showReportDialog(year) {
            this.$refs.reportDialog.show(year)
        },
        downloadAbstract() {
            const abstractWS = XLSX.utils.json_to_sheet(this.abstracts)
            const growthWS = this.buildXlsxSheet(this.growthData)
            const drawdownWS = this.buildXlsxSheet(this.drawdownData)
            const returnWS = this.buildXlsxSheet(this.returnData)

            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, abstractWS, '요약')
            XLSX.utils.book_append_sheet(wb, growthWS, '자산변동')
            XLSX.utils.book_append_sheet(wb, drawdownWS, 'MDD')
            XLSX.utils.book_append_sheet(wb, returnWS, '연도별 수익률')

            XLSX.writeFile(wb, '백테스트_요약보고서.xlsx')
        },
        downloadDetails() {
            const wb = XLSX.utils.book_new()
            for (const detail of this.detailReports) {
                const sheetName = detail[0]

                const items = []
                const nameMap = {}
                Object.keys(detail[1][0][1]).forEach(key => {
                    nameMap[key] = detail[1][0][1][key].name
                })
                for (const ele of detail[1]) {
                    const item = {
                        date: ele[0]
                    }

                    for (const code in ele[1]) {
                        item[`${nameMap[code]}(${code})`] = ele[1][code].amount
                    }

                    items.push(item)
                }

                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(items, {
                    header: ['date']
                }), sheetName)
            }

            XLSX.writeFile(wb, '백테스트_상세보고서.xlsx')
        },
        buildXlsxSheet(rawData) {
            const jsonData = []
            for (let i = 0; i < rawData.labels.length; i++) {
                const date = rawData.labels[i];
                const mine = rawData.datasets[0].data[i];
                const kospi = rawData.datasets[1].data[i];
                const kosdaq = rawData.datasets[2].data[i];
                jsonData.push({
                    date,
                    mine,
                    kospi,
                    kosdaq
                })
            }

            const ws = XLSX.utils.json_to_sheet(jsonData)
            for (const key in ws) {
                if (key.startsWith('!') || key.substring(1) == '1') continue

                if (!key.startsWith('A')) {
                    ws[key]['t'] = 'n'
                }
            }

            return ws
        },
        copyToClipboard() {
            this.copyLoading = true

            html2canvas(this.$refs.report, {
                    scrollX: 0,
                    scrollY: -window.scrollY,
                }).then(canvas => {
                    canvas.toBlob(blob => navigator.clipboard.write([new ClipboardItem({
                        'image/png': blob
                    })]))
                    this.$toasted.success('클립보드에 복사됐습니다')
                }).catch(e => {
                    this.$toasted.error('이미지 복사 중에 오류가 발생했습니다')
                })
                .finally(() => this.copyLoading = false)
        },
    }
}
</script>

<style>
.report-container {
    border-radius: 5px;
    border: 1px solid #f0f0f0;
}

.graph-header {
    font-size: 18px;
    color: white;
    padding: 10px;
    background: #3498db;
}
</style>
